import { ParseKeys } from '@if/core/i18n'
import { Banner, BodyText, useDialog, useIfUser } from '@if/mui'
import { Stack } from '@mui/material'
import { useComputeCalendarAction } from '@src/api/loan/loan-api'
import { EPaymentMethod } from '@src/types/constants'
import { ECalendarSimulationAction, SignedCalendarSimulation } from '@src/types/loan/loan-simulation'
import { RotateCcw } from 'lucide-react'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

/**
 * Why this calendar cannot be reset, or `undefined` when it can. Answered before the agent
 * commits to anything: a reset that would be refused says so in the dialog that offers it.
 */
const useResetRefusal = (signedSimulation: SignedCalendarSimulation): ParseKeys | undefined => {
  const user = useIfUser()
  const canBypassAcpLock = user.rights.canEditLockedPpaPayments

  return useMemo((): ParseKeys | undefined => {
    const { simulation } = signedSimulation
    if (simulation.actions.length !== 0) return 'loan.payment.resetDialog.errors.hasPreviousActions'

    const { expectedPayments } = simulation.fullLoan.expectedPaymentsCalendar
    const lastExpectedPaymentDate = expectedPayments?.length
      ? expectedPayments[expectedPayments.length - 1].date
      : undefined

    const minimumDate = simulation.getNextValidActionDate(EPaymentMethod.PreAuthorizedDebit, canBypassAcpLock)

    // Last expected payment has been made or is in the scope of the ACP processing.
    if (!lastExpectedPaymentDate || lastExpectedPaymentDate < minimumDate)
      return 'loan.payment.resetDialog.errors.pastLastExpectedPaymentDate'
    if (simulation.hasExecutedPaymentsAfterDate(minimumDate))
      return 'loan.payment.resetDialog.errors.hasCompletedPaymentInFutur'

    return undefined
  }, [signedSimulation, canBypassAcpLock])
}

/**
 * The one calendar dialog with nothing to fill in: it states what a reset would do, states
 * why it cannot run when it cannot, and confirms. Built on `useDialog` rather than
 * `useConfirmDialog` for one reason — the reset *is* a recompute, so confirming has to
 * resolve with the calendar it produced, which a yes/no confirm cannot carry.
 */
export const useLoanCalendarResetDialog = (
  originalSignedSimulation: SignedCalendarSimulation,
  isLockedByOther: boolean,
) => {
  const { t } = useTranslation()
  const { loan } = originalSignedSimulation.simulation.fullLoan
  const { mutateAsync: computeAction } = useComputeCalendarAction(loan.id, loan.financingCompanyId)
  const refusal = useResetRefusal(originalSignedSimulation)

  return useDialog<void, SignedCalendarSimulation>({
    icon: RotateCcw,
    title: t('loan.payment.reset'),
    confirmLabel: t('loan.payment.reset'),
    confirmTone: 'destructive',
    maxWidth: 'sm',
    fullWidth: true,
    content: (
      <Stack spacing={2}>
        {refusal && <Banner severity="error">{t(refusal)}</Banner>}
        <BodyText>{t('loan.payment.resetConfirm')}</BodyText>
      </Stack>
    ),
    confirmDisabled: Boolean(refusal) || isLockedByOther,
    confirmTooltip: isLockedByOther ? t('common.lockTooltip.lockedByOther') : refusal ? t(refusal) : null,
    onConfirm: () =>
      computeAction({
        ...originalSignedSimulation,
        action: { actionType: ECalendarSimulationAction.Reset },
      }),
  })
}
